// These helpers are calling this template's own server-side routes
// so, they are not directly calling Marketplace API or Integration API.
// You can find these api endpoints from 'server/api/...' directory

import appSettings from '../config/settings';
import { types as sdkTypes, transit } from './sdkLoader';
import Decimal from 'decimal.js';

export const apiBaseUrl = marketplaceRootURL => {
  const port = process.env.REACT_APP_DEV_API_SERVER_PORT;
  const useDevApiServer = process.env.NODE_ENV === 'development' && !!port;

  // In development, the dev API server is running in a different port
  if (useDevApiServer) {
    return `http://localhost:${port}`;
  }

  // Otherwise, use the given marketplaceRootURL parameter or the same domain and port as the frontend
  return marketplaceRootURL ? marketplaceRootURL.replace(/\/$/, '') : `${window.location.origin}`;
};

// Application type handlers for JS SDK.
//
// NOTE: keep in sync with `typeHandlers` in `server/api-util/sdk.js`
export const typeHandlers = [
  // Use Decimal type instead of SDK's BigDecimal.
  {
    type: sdkTypes.BigDecimal,
    customType: Decimal,
    writer: v => new sdkTypes.BigDecimal(v.toString()),
    reader: v => new Decimal(v.value),
  },
];

const serialize = data => {
  return transit.write(data, { typeHandlers, verbose: appSettings.sdk.transitVerbose });
};

const deserialize = str => {
  return transit.read(str, { typeHandlers });
};

const methods = {
  POST: 'POST',
  GET: 'GET',
  PUT: 'PUT',
  PATCH: 'PATCH',
  DELETE: 'DELETE',
};

// If server/api returns data from SDK, you should set Content-Type to 'application/transit+json'
const request = (path, options = {}) => {
  const url = `${apiBaseUrl()}${path}`;
  const { credentials, headers, body, ...rest } = options;

  // If headers are not set, we assume that the body should be serialized as transit format.
  const shouldSerializeBody =
    (!headers || headers['Content-Type'] === 'application/transit+json') && body;
  const bodyMaybe = body ? { body: shouldSerializeBody ? serialize(body) : body } : {};

  const fetchOptions = {
    credentials: credentials || 'include',
    // Since server/api mostly talks to Marketplace API using SDK,
    // we default to 'application/transit+json' as content type (as SDK uses transit).
    headers: headers || { 'Content-Type': 'application/transit+json' },
    ...bodyMaybe,
    ...rest,
  };

  return window.fetch(url, fetchOptions).then(res => {
    const contentTypeHeader = res.headers.get('Content-Type');
    const contentType = contentTypeHeader ? contentTypeHeader.split(';')[0] : null;

    if (res.status >= 400) {
      return res.json().then(data => {
        let e = new Error();
        e = Object.assign(e, data);

        throw e;
      });
    }
    if (contentType === 'application/transit+json') {
      return res.text().then(deserialize);
    } else if (contentType === 'application/json') {
      return res.json();
    }
    return res.text();
  });
};

// Keep the previous parameter order for the post method.
// For now, only POST has own specific function, but you can create more or use request directly.
const post = (path, body, options = {}) => {
  const requestOptions = {
    ...options,
    method: methods.POST,
    body,
  };

  return request(path, requestOptions);
};

// Fetch transaction line items from the local API endpoint.
//
// See `server/api/transaction-line-items.js` to see what data should
// be sent in the body.
export const transactionLineItems = body => {
  return post('/api/transaction-line-items', body);
};

const supportPost = (path, body) =>
  post(path, JSON.stringify(body), { headers: { 'Content-Type': 'application/json' } });

export const sendContactSubmission = body => supportPost('/api/contact', body);

export const sendReportSubmission = body => supportPost('/api/report', body);

// The postal code is sent as JSON (see server/api/shipping-origin.js), so the body must be
// stringified: sending a raw object here makes fetch send the literal string "[object Object]",
// which the server rejects with a 400 and the origin is silently never saved.
export const saveShippingOrigin = postalCode =>
  post('/api/shipping-origin', JSON.stringify({ postalCode }), {
    headers: { 'Content-Type': 'application/json' },
  });
export const getMyShippingOrigin = () => request('/api/shipping-origin');

/** Read seller-authorized preparation data without buyer delivery details. */
export const getPackageConfirmation = transactionId =>
  request(`/api/package-confirmations/${encodeURIComponent(transactionId)}`);

/** Confirm packed measurements; never requests quotes, labels or payment changes. */
export const confirmPackageMeasurements = (transactionId, body) =>
  post(`/api/package-confirmations/${encodeURIComponent(transactionId)}`, JSON.stringify(body), {
    headers: { 'Content-Type': 'application/json' },
  });

/** Read only the authenticated seller's private address for editing their settings. */
export const getMyPrivateShippingOrigin = () => request('/api/shipping-origin?details=true');

/** Read owner-only shipping preparation; this never requests postage or carrier writes. */
export const getSellerShipment = transactionId =>
  request(`/api/shipments/${encodeURIComponent(transactionId)}`);

/** Store private sender details without passing them to Sharetribe profile data. */
export const savePrivateShippingOrigin = address =>
  post('/api/shipping-origin', JSON.stringify({ address }), {
    headers: { 'Content-Type': 'application/json' },
  });

export const getPurchaseAvailability = listingIds =>
  post('/api/purchase-availability', JSON.stringify({ listingIds }), {
    headers: { 'Content-Type': 'application/json' },
  });

export const reconcilePurchase = transactionId =>
  post('/api/purchase-availability/reconcile', JSON.stringify({ transactionId }), {
    headers: { 'Content-Type': 'application/json' },
  });

export const getCanadaPostShippingStatus = () => request('/api/shipping-rates');

export const getCanadaPostRates = body =>
  post('/api/shipping-rates', JSON.stringify(body), {
    headers: { 'Content-Type': 'application/json' },
  });

const shopOrderPost = (path, body = {}) =>
  post(path, JSON.stringify(body), { headers: { 'Content-Type': 'application/json' } });

export const createShopOrder = body => shopOrderPost('/api/shop-orders', body);
export const reserveShopOrder = checkoutToken =>
  shopOrderPost(`/api/shop-orders/${encodeURIComponent(checkoutToken)}/reserve`);
export const getShopOrder = checkoutToken =>
  request(`/api/shop-orders/${encodeURIComponent(checkoutToken)}`);
export const getShopOrderBreakdown = checkoutToken =>
  request(`/api/shop-orders/${encodeURIComponent(checkoutToken)}/breakdown`);
export const quoteShopOrderShipping = (checkoutToken, destination) =>
  shopOrderPost(`/api/shop-orders/${encodeURIComponent(checkoutToken)}/shipping-quote`, {
    destination,
  });
export const initiateShopOrder = (checkoutToken, body) =>
  shopOrderPost(`/api/shop-orders/${encodeURIComponent(checkoutToken)}/initiate`, body);
export const finalizeShopOrder = checkoutToken =>
  shopOrderPost(`/api/shop-orders/${encodeURIComponent(checkoutToken)}/finalize`);

export const notifyListingPendingApproval = (listingId, submissionId) =>
  post(
    `/api/moderation-notifications/listings/${encodeURIComponent(listingId)}/pending-approval`,
    JSON.stringify({ submissionId }),
    { headers: { 'Content-Type': 'application/json' } }
  );

// Ask the server to email PageHop support about a dispute the current user just opened. The server
// verifies the transaction itself; callers should not block on or surface failures from this.
export const notifyOrderDisputed = transactionId =>
  post(
    `/api/dispute-notifications/transactions/${encodeURIComponent(transactionId)}`,
    JSON.stringify({}),
    { headers: { 'Content-Type': 'application/json' } }
  );

// Check whether a My Shelf book already has a marketplace listing before selling it.
// Pass `listingId` for the draft being published so it is not counted as the existing listing.
export const checkShelfItemListing = (collectionItemId, listingId) =>
  post(
    '/api/my-shelf/sell-check',
    JSON.stringify({ collection_item_id: collectionItemId, listing_id: listingId || undefined }),
    { headers: { 'Content-Type': 'application/json' } }
  );

// Persist the My Shelf → marketplace listing link for a listing created from a shelf book.
export const linkShelfItemToListing = (collectionItemId, listingId) =>
  post(
    '/api/my-shelf/link-listing',
    JSON.stringify({ collection_item_id: collectionItemId, listing_id: listingId }),
    { headers: { 'Content-Type': 'application/json' } }
  );

// Initiate a privileged transaction.
//
// With privileged transitions, the transactions need to be created
// from the backend. This endpoint enables sending the order data to
// the local backend, and passing that to the Marketplace API.
//
// See `server/api/initiate-privileged.js` to see what data should be
// sent in the body.
export const initiatePrivileged = body => {
  return post('/api/initiate-privileged', body);
};

// Transition a transaction with a privileged transition.
//
// This is similar to the `initiatePrivileged` above. It will use the
// backend for the transition. The backend endpoint will add the
// payment line items to the transition params.
//
// See `server/api/transition-privileged.js` to see what data should
// be sent in the body.
export const transitionPrivileged = body => {
  return post('/api/transition-privileged', body);
};

// Create user with identity provider (e.g. Facebook or Google)
//
// If loginWithIdp api call fails and user can't authenticate to Marketplace API with idp
// we will show option to create a new user with idp.
// For that user needs to confirm data fetched from the idp.
// After the confirmation, this endpoint is called to create a new user with confirmed data.
//
// See `server/api/auth/createUserWithIdp.js` to see what data should
// be sent in the body.
export const createUserWithIdp = body => {
  return post('/api/auth/create-user-with-idp', body);
};

// Check if user can be deleted and then delete the user. Endpoint logic
// must be modified to accommodate the transaction processes used in
// the marketplace.
export const deleteUserAccount = body => {
  return post('/api/delete-account', body);
};

// Signed-in member's PageHop Rewards (Shelf Points) summary. Identity comes from the session.
export const getRewardsSummary = () => request('/api/rewards/summary');

export const getFollowSummary = userId => request(`/api/follows/${userId}`);

export const followUser = userId =>
  request(`/api/follows/${userId}`, {
    method: methods.POST,
  });

export const unfollowUser = userId =>
  request(`/api/follows/${userId}`, {
    method: methods.DELETE,
  });
